// App-wide design tokens — single source of truth for the CVF palette.
// Promoted from the CRM module so every screen shares one brand + atom set.
// Import `crm` for raw values, `CrmAvatar`/`Pill`/`ResponsibleChip` for shared UI atoms.

import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

export const crm = {
  // brand — single accent used across the app
  brand:        "#2563eb",
  brandDark:    "#1d4ed8",
  brandSoft:    "#eff4ff",
  // surfaces
  pageBg:       "#f6f8fc",
  surface:      "#ffffff",
  columnBg:     "#f1f5f9",
  subtle:       "#f8fafc",
  // lines + text
  border:       "#e6e9ef",
  borderStrong: "#d6dbe5",
  text:         "#0f172a",
  textMuted:    "#64748b",
  textFaint:    "#94a3b8",
  // semantic
  success:      "#16a34a",
  danger:       "#dc2626",
  warning:      "#d97706",
  // elevation
  shadowSm:     "0 1px 2px rgba(16,24,40,.06)",
  shadowMd:     "0 4px 12px rgba(16,24,40,.10)",
  shadowLg:     "0 12px 32px rgba(16,24,40,.16)",
  radius:       "12px",
  radiusSm:     "8px",
};

// Initials helper: "João Silva" → "JS"
export const initials = (name?: string) =>
  (name ?? "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";

// Deterministic soft color for an avatar from a string
export const avatarColor = (seed?: string) => {
  const palette = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#16a34a", "#0891b2", "#ca8a04"];
  let h = 0;
  for (const c of seed ?? "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
};

// ── Shared UI atoms ───────────────────────────────────────────────────────────

/** Initials circle avatar (28px default). */
export const CrmAvatar = ({
  name,
  size = 28,
}: {
  name?: string;
  size?: number;
}) => (
  <Flex
    align="center"
    justify="center"
    borderRadius="full"
    bg={avatarColor(name)}
    color="white"
    fontWeight="700"
    fontSize="11px"
    flexShrink={0}
    style={{ width: size, height: size }}
  >
    {initials(name)}
  </Flex>
);

/** Status / label chip — rounded-full, normal case. */
export const Pill = ({
  children,
  bg = crm.subtle,
  color = crm.textMuted,
  borderColor,
}: {
  children: React.ReactNode;
  bg?: string;
  color?: string;
  borderColor?: string;
}) => (
  <Box
    display="inline-flex"
    alignItems="center"
    gap="4px"
    px="8px"
    py="2px"
    borderRadius="full"
    fontSize="11px"
    fontWeight="500"
    bg={bg}
    color={color}
    border={borderColor ? `1px solid ${borderColor}` : undefined}
    style={{ textTransform: "none", whiteSpace: "nowrap" }}
  >
    {children}
  </Box>
);

/** Inline Avatar + name combo used in cards/tables. */
export const ResponsibleChip = ({ name }: { name?: string }) =>
  name ? (
    <Flex align="center" gap="5px" display="inline-flex">
      <CrmAvatar name={name} size={20} />
      <Text fontSize="11px" fontWeight="500" color={crm.textMuted} style={{ textTransform: "none" }}>
        {name}
      </Text>
    </Flex>
  ) : null;
